import { View, Platform, Pressable } from 'react-native'
import React, { useEffect, useRef, useState } from 'react'
import useVideoControls from './useVideoControls'
import { trpc } from 'api'
import { VideoView } from 'expo-video'
import cn from '@ui/utils/cn'
import { colors } from '@ui/colors'
import PressableIcon from '@ui/buttons/PressableIcon'
import Animated from 'react-native-reanimated'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import { useIsFocused } from '@ui/hooks/useIsFocused'

interface Props {
  url: string
  autoplay?: boolean
  className?: string
}

export default function VideoContainer({ url, autoplay, className }: Props) {
  const [isOpenControls, setOpenControls] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { data } = trpc.vimeo.getVideoFromUrl.useQuery({ url })
  const focused = useIsFocused()
  const file = data?.files?.find(
    (f) =>
      f.quality ===
      Platform.select({
        default: 'hls',
        web: 'hd',
      })
  )

  const { player, isPlaying, play, pause, replay } = useVideoControls({
    autoplay,
  })

  // Replace source when the resolved Vimeo URL becomes available
  useEffect(() => {
    if (file?.link) {
      player.replace({ uri: file.link })
    }
  }, [file?.link, player])

  // Sync muted state with screen focus
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability
    player.muted = !focused
  }, [focused, player])

  const styleControls = useAnimatedOpacity(isOpenControls)

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (isOpenControls) setOpenControls(false)
    }, 5000)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isOpenControls])

  return (
    <>
      <View
        className={cn(
          'absolute left-0 top-0 h-full w-full overflow-hidden rounded-2xl bg-gray-75',
          className
        )}
      >
        <View className="absolute h-full w-full items-center justify-center">
          <LoadingFooter className="p-0" />
        </View>
        <VideoView
          player={player}
          style={{
            alignSelf: 'center',
            flex: 1,
            width: '100%',
            height: '100%',
          }}
          contentFit="cover"
        />
        <Pressable
          onPress={() => setOpenControls((prev) => !prev)}
          className="absolute left-0 top-0 h-full w-full"
        >
          <Animated.View
            className="flex h-full w-full items-center justify-center bg-black/50"
            style={styleControls}
          >
            <PressableIcon
              className="relative h-32 w-32"
              name={isPlaying ? 'player-pause-filled' : 'player-play-filled'}
              color={colors.white}
              sizeIcon={64}
              onPress={isPlaying ? pause : play}
            />
            <PressableIcon
              className="absolute -bottom-16 h-12 w-12"
              name="repeat"
              color={colors.white}
              onPress={replay}
            />
          </Animated.View>
        </Pressable>
      </View>
    </>
  )
}
